/*
 * WHAT A MEETING IS, from its calendar title.
 *
 * The cheap tier of a two-tier plan. Titles Gabriel actually has look like
 * "Gambit/Remotion kickoff", "DataHub <> Remotion Weekly Call", "Hush / Remotion -
 * Bi-weekly Call", "follow up on LI Ads", "Medable/Remotion". A keyword match reads the
 * first three; the last one has nothing to say and must therefore say nothing.
 *
 * THE EMPTY SLOT IS THE POINT. "Medable/Remotion" on the Medable row repeats the company
 * name back at itself — furniture that looks like information. An empty slot is honest.
 *
 * The proper tier is classifyCall on the transcript, which could name the purpose
 * post-hoc. That is enrichment, and it is ledgered, not built.
 */

// Order matters: the first match wins, so the specific patterns precede the general.
// Bi-weekly before weekly, or "Bi-weekly Call" reads as "Weekly call".
const CADENCE = [
  [/\b(bi-?weekly|fortnightly)\b/i, "Bi-weekly"],
  [/\b(quarterly|qbr)\b/i, "Quarterly"],
  [/\bmonthly\b/i, "Monthly"],
  [/\bweekly\b/i, "Weekly"],
  [/\bdaily\b/i, "Daily"],
];

// What KIND of thing the recurring meeting is, when the title bothers to say.
const NOUN = [
  [/\breport(ing)?\b/i, "report"],
  [/\breview\b/i, "review"],
  [/\bcatch-?up\b/i, "catch-up"],
  [/\bsync\b/i, "sync"],
  [/\bstandup\b/i, "standup"],
];

// One-off meeting types, each with a name of its own.
const KINDS = [
  [/\bkick-?off\b/i, "Kickoff"],
  [/\bintro(duction|ductory)?\b/i, "Intro call"],
  [/\b(proposal|pricing|quote)\b/i, "Pricing review"],
  [/\bscop(e|ing)\b/i, "Scope call"],
  [/\bonboard(ing)?\b/i, "Onboarding"],
  [/\bdemo\b/i, "Demo"],
  [/\bworkshop\b/i, "Workshop"],
];

/** Strip punctuation and spacing so two names can be compared for sameness. */
function squash(text) {
  return String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/*
 * Is the title just the two company names and a separator? "Medable/Remotion",
 * "DataHub <> Remotion", "Litmus + Remotion", "Remotion x HiBob" — the whole family,
 * detected by removing our name and the company's and seeing whether anything is left.
 * That uses the company name we already have rather than a list of separator characters
 * we would have to keep extending.
 */
function isJustTheNames(title, companyName) {
  const left = squash(title).replace(/remotion/g, "");
  if (!left) return true;
  const co = squash(companyName);
  if (co && left === co) return true;
  // Also "medablecall", "hushbiweeklycall" would have matched a keyword already, so
  // anything reaching here with the company name plus one or two stray characters
  // (an x, an ampersand, a hyphen) is still just the names.
  if (co && left.startsWith(co) && left.length - co.length <= 2) return true;
  if (co && left.endsWith(co) && left.length - co.length <= 2) return true;
  return false;
}

/**
 * A short label for what the meeting is, or null when the title says nothing beyond
 * who is on the call.
 *
 * Pure and dependency-free so it can be unit tested without a browser.
 */
export function meetingPurpose(title, companyName = null) {
  const raw = String(title || "").trim();
  if (!raw) return null;

  for (const [re, label] of KINDS) {
    if (re.test(raw)) return label;
  }

  for (const [re, cadence] of CADENCE) {
    if (!re.test(raw)) continue;
    const noun = NOUN.find(([n]) => n.test(raw));
    return `${cadence} ${noun ? noun[1] : "call"}`;
  }

  // A noun with no cadence in front of it is still a purpose: "Litmus + Remotion Sync".
  const bare = NOUN.find(([re]) => re.test(raw));
  if (bare) return bare[1].charAt(0).toUpperCase() + bare[1].slice(1);

  // No keyword. If the title is only the participants' names, the slot stays empty
  // rather than repeating what the row already says.
  if (isJustTheNames(raw, companyName)) return null;

  // Something else a human typed — "follow up on LI Ads". Real information, shown as
  // written, capped so a long title cannot push the row's other columns around.
  return raw.length > 34 ? `${raw.slice(0, 33).trimEnd()}…` : raw;
}

export default meetingPurpose;
